/**
 * Mappt Mollie failureReason-Codes (https://docs.mollie.com/payments/status-changes)
 * auf i18n-Keys im `mollieCheckout.errorCodes.*`-Namespace.
 *
 * Phase 9 D-22: Client-Side-Mapping (keine Backend-Query, Codes sind stabil dokumentiert).
 * Phase 9 D-23: Unknown-Code -> Fallback `unknown` + console.warn fuer spaeteres Mapping-Update.
 */

export const MOLLIE_ERROR_CODE_KEYS = [
    "insufficientFunds",
    "cardExpired",
    "cardDeclined",
    "invalidCardNumber",
    "invalidCvv",
    "authenticationFailed",
    "canceledByConsumer",
    "paymentRefused",
    "inactiveCard",
    "cardBlocked",
    "generalError",
    "possibleFraud",
    "invalidAmount",
    "unknownReason",
    "unknown",
] as const;

const CODE_LOOKUP: Readonly<Record<string, string>> = MOLLIE_ERROR_CODE_KEYS.reduce(
    (acc, code) => {
        acc[code.toLowerCase()] = code;
        return acc;
    },
    {} as Record<string, string>,
);

/**
 * Mollie-Codes, deren Wort-Reihenfolge nicht zu unseren i18n-Keys passt.
 * Beispiel: Mollie sendet "expired_card" (Adjektiv-first), unser i18n-Key
 * ist "cardExpired" (Substantiv-first). Nach .replace(/_/g, "") wuerde
 * "expiredcard" niemals auf "cardexpired" matchen — daher dieser Alias.
 *
 * Schluessel: Mollie-Code (snake_case, lowercase).
 * Wert: Lookup-Key aus CODE_LOOKUP (camelCase, ebenfalls lowercase).
 */
const SNAKE_TO_LOOKUP_ALIAS: Readonly<Record<string, string>> = {
    expired_card: "cardexpired",
};

export function mapMollieFailureReasonToKey(code: string | null | undefined): string {
    if (code === null || code === undefined || code === "") {
        return "mollieCheckout.errorCodes.unknown";
    }
    // Mollie liefert Failure-Codes in snake_case (z.B. "card_declined",
    // "insufficient_funds"). CODE_LOOKUP-Keys sind dagegen lowercase camelCase
    // ohne Underscore ("carddeclined", "insufficientfunds") — siehe Reduce in
    // Z. 27-33. Underscore-Strip ist daher Pflicht, sonst landen ALLE realen
    // Mollie-Codes auf "unknown" und der Kunde sieht statt der spezifischen
    // Fehlermeldung nur den generischen Fallback.
    const trimmed = String(code).toLowerCase().trim();
    const normalized = SNAKE_TO_LOOKUP_ALIAS[trimmed] ?? trimmed.replace(/_/g, "");
    const knownCode = CODE_LOOKUP[normalized];
    if (knownCode && knownCode !== "unknown") {
        return `mollieCheckout.errorCodes.${knownCode}`;
    }
    // Unmapped: warn fuer spaeteres Update + Fallback
    // eslint-disable-next-line no-console
    console.warn("Unmapped Mollie failureReason", {code});
    return "mollieCheckout.errorCodes.unknown";
}
